<template>
  <div class="answer-note">
    <button
      type="button"
      class="answer-note__toggle"
      :aria-expanded="isOpen"
      :aria-controls="blockId"
      @click="toggle"
    >
      <span class="answer-note__plus" aria-hidden="true">+</span>
      <span>{{ t('collection.answer.optional.toggle') }}</span>
    </button>

    <!-- An inline disclosure, never a sheet: zero overlays on the answer path is
         a measured requirement of this design, not a preference. The uploader is
         driven straight to the file picker for the same reason — AvatarMediaSelector
         wraps the same uploader in an f7-sheet, which is why it is NOT used here. -->
    <div v-show="isOpen" :id="blockId" class="answer-note__block">
      <label class="answer-note__label" :for="fieldId">{{ t('collection.note.label') }}</label>
      <textarea
        :id="fieldId"
        ref="field"
        v-model="text"
        class="answer-note__field"
        :placeholder="t('collection.note.placeholder')"
        :disabled="disabled"
        rows="2"
        maxlength="1000"
        @input="onInput"
      />

      <div v-if="isOpen" class="answer-note__photo">
        <avatar-file-pond-uploader
          ref="uploader"
          :accepted-file-types="ACCEPTED_TYPES"
          max-file-size="5MB"
          :placeholder-text="t('collection.answer.optional.photo')"
          @click="pickPhoto"
          @update:model-value="onToken"
          @upload-error="onUploadError"
          @remove="onPhotoRemoved"
        ></avatar-file-pond-uploader>

        <!-- Her photo from an EARLIER answer, so she can see there is one on
             record. Shown only while she has not attached a replacement. It
             needs no caveat: since the 2026-08-12 backend change a revision that
             omits `photo_token` carries the stored photo forward. -->
        <figure v-if="showPriorPhoto" class="answer-note__prior">
          <!-- The alt of a photo ALREADY ON RECORD, not the action label: this
               image is one she has sent, and «ضيف صورة» ("add a photo") would
               describe the button beside it instead of the thing itself. -->
          <img :src="photoUrl" :alt="t('collection.answer.optional.photoAlt')" />
        </figure>
      </div>

      <!-- The uploader's own message (the backend's, or its Arabic fallback).
           An upload failure is NOT an answer failure — the controls stay live. -->
      <p v-if="uploadError" class="answer-note__error" role="alert">{{ uploadError }}</p>

      <p class="answer-note__hint">{{ t('collection.answer.optional.hint') }}</p>
    </div>
  </div>
</template>

<script setup>
/**
 * OptionalNote — the one collapsed affordance on an answerable card: a note and
 * a photo behind a single "+". Nothing is on screen until she asks for it, and
 * it never gates submit — a note-less, photo-less answer is a complete answer.
 *
 * INLINE, not a sheet. Zero overlays on the answer path is one of the measured
 * bars this redesign is held to, so this is a plain disclosure — and the reason
 * the uploader is driven straight to the file picker rather than through
 * `AvatarMediaSelector`, which wraps the same uploader in an `f7-sheet`.
 *
 * ── The photo (BE→FE 2026-08-11) ────────────────────────────────────────────
 * No multipart and no new endpoint: `POST /api/v1/filepond` returns an encrypted
 * token string, and that token rides in the answer payload as `photo_token` —
 * the same contract wallet receipts and registration already use. The upload
 * runs WHILE she composes her note, so at submit time there is nothing left to
 * wait for; with multipart she would have waited after pressing send, on mobile
 * data, which is the worse moment.
 *
 * Two rules the token forces:
 *  - It is SINGLE-USE and expires after 60 minutes. A consumed or expired one is
 *    a 422, never a silent drop, so an abandoned card past that hour needs a
 *    re-upload.
 *  - An upload failure is not an answer failure. The message surfaces inline and
 *    the answer controls stay live — she can still answer without a photo.
 *
 * HEIC is deliberately NOT allow-listed. iPhones shoot it by default and
 * browsers usually — not always — convert on upload; the backend would rather
 * see real rejections than store a format nothing can display.
 *
 * ── A revision KEEPS her photo (BE→FE 2026-08-12) ───────────────────────────
 * Omission is the keep signal: a revision that omits `photo_token` carries the
 * stored photo forward, and one that omits `note` carries the stored note. This
 * component therefore shows an existing photo without any re-attach caveat —
 * the warning it used to carry (`answer.optional.photoKeep`) described a loss
 * that no longer happens, and the string is deleted.
 *
 * ── The prefill contract (carried over from IssueCard verbatim) ──────────────
 * Seed from `note` on an IMMEDIATE watch — so a revision opens holding what she
 * already told us, and reactivity survives — then STOP adopting server values the
 * moment she touches the control. The card is re-read on screen re-entry and
 * pull-to-refresh; without the dirty flag, a background refresh would silently
 * overwrite whatever she is in the middle of typing.
 *
 * That is also why `note` must be bound to the SERVER value (`memberComment`) and
 * never to the parent's own copy of what she has typed: this component is the
 * one that owns the editing state, and it reports outward through `update:note`.
 *
 * `maxlength` is 1000 — the backend's real ceiling
 * (`ItemIssueService::MEMBER_TEXT_MAX_LENGTH`), which THROWS rather than
 * truncating. Today's card caps at 280 and silently loses the rest of a longer
 * answer; 1000 both restores that text and keeps a 422 she could not diagnose
 * from ever being reachable.
 */
import { computed, defineAsyncComponent, nextTick, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';


const props = defineProps({
  // The SERVER value (`memberComment`) — not the parent's copy. See the contract
  // in the docblock above.
  note: {
    type: String,
    default: null,
  },
  // Her OWN photo from an earlier answer (`memberPhotoUrl`), when one exists.
  // Read-only: it is displayed so she can see there is one on record.
  photoUrl: {
    type: String,
    default: null,
  },
  // Open on mount — a revision opens holding her note rather than hiding it
  // behind an affordance she has already used once.
  startOpen: {
    type: Boolean,
    default: false,
  },
  // A submit is in flight, or a refusal locked the answer. The BLOCK stays open
  // and keeps her typed note either way (spec Section 9) — only the field goes inert.
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:note', 'update:photo-token']);

/**
 * LAZY, and that is load-bearing. `IssueDecisionPage` is an EAGER `component:`
 * route — a push deep-link can make it the boot URL — so a static import here
 * would pull filepond, vue-filepond, two plugins and filepond's CSS into the
 * initial bundle of every cold start. Every other consumer of this uploader
 * (wallet, registration, orders) sits behind an `asyncComponent:` route, so it
 * is not in that chunk today and must not start being.
 *
 * Paired with `v-if="isOpen"` on the photo block below: the chunk is fetched
 * when she opens the optional block, not when the card mounts.
 */
const AvatarFilePondUploader = defineAsyncComponent(
  () => import('@/components/common/AvatarFilePondUploader.vue'),
);

// Spec section 13. HEIC is deliberately absent — see the docblock above.
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const { t } = useI18n({ useScope: 'global' });

// Stable, collision-free ids so aria-controls / label-for hold even when two
// cards ever share a page.
const uid = useId();
const blockId = computed(() => `ci-note-block-${uid}`);
const fieldId = computed(() => `ci-note-field-${uid}`);

const field = ref(null);
const text = ref('');
// Set the moment she types, and never cleared: from then on the server value is
// stale by definition and must not win.
const dirty = ref(false);
const isOpen = ref(false);

watch(
  () => props.note,
  (note) => {
    if (dirty.value) return;
    text.value = note ?? '';
    // Open for a note that already exists, but never auto-CLOSE: collapsing the
    // block under her would hide text she can see on screen.
    if (text.value !== '') isOpen.value = true;
    // REPORT THE ADOPTED VALUE, not just a typed one, and SAY it was adopted.
    //
    // The flag matters as much as the value. The parent decides whether an empty
    // field is a deliberate CLEAR, and that can only be true of a note she has
    // actually seen — a server note that arrives while she is mid-edit is never
    // adopted here, so the parent must not count it as something she emptied.
    // Without the flag the parent cannot tell this emit from a keystroke.
    //
    // The backend overwrites
    // `member_payload` WHOLESALE on every revision
    // (`ItemIssueService::recordMemberResponse` → `'member_payload' => $normalized`,
    // and `normalizeMemberPayload` only carries `note` when the payload has one).
    // So a revision that omits `note` DELETES the note already on record. Without
    // this emit the parent's `noteText` stays '' until she types, and re-tapping a
    // control — the documented way to change only an answer — silently destroys a
    // note she can see in the open textarea in front of her. Verified in-browser:
    // the field held «مقاس ٤٢ لو سمحت» and the POST carried `payload: {}`.
    emit('update:note', text.value, { adopted: true });
  },
  { immediate: true },
);

// A photo already on record opens the block for the same reason a note does:
// collapsing it would hide something she has on file behind an affordance she
// has already used once.
watch(
  () => props.photoUrl,
  (url) => {
    if (url) isOpen.value = true;
  },
  { immediate: true },
);

// Immediate, and NOT read once at setup: the card mounts before the issue
// resolves, so `startOpen` routinely flips from false to true one tick later.
// Reading it in root scope would open on a fast cache hit and silently miss on
// every cold load — the exact reactivity loss the linter flags.
watch(
  () => props.startOpen,
  (open) => {
    if (open) isOpen.value = true;
  },
  { immediate: true },
);

async function toggle() {
  isOpen.value = !isOpen.value;
  if (!isOpen.value) return;
  // Opening is a deliberate act — put the caret where she meant to type.
  await nextTick();
  field.value?.focus?.();
}

function onInput() {
  dirty.value = true;
  emit('update:note', text.value, { adopted: false });
}

// ── The photo ───────────────────────────────────────────────────────────────

const uploader = ref(null);
const uploadError = ref(null);
// The FilePond serverId — an encrypted, single-use token, not a path.
const photoToken = ref('');

// Her existing photo is worth showing only while it is still the photo on
// record. The moment she attaches a replacement, the uploader beside it is
// already showing the one that will win.
const showPriorPhoto = computed(() => !!props.photoUrl && photoToken.value === '');

/** The uploader emits `click`; opening the OS picker is the parent's call —
 *  which is exactly the seam that lets this avoid a sheet. */
function pickPhoto() {
  if (props.disabled) return;
  uploadError.value = null;
  uploader.value?.triggerFilePond?.();
}

function onToken(serverId) {
  photoToken.value = serverId || '';
  if (photoToken.value) uploadError.value = null;
  emit('update:photo-token', photoToken.value);
}

function onPhotoRemoved() {
  uploadError.value = null;
  photoToken.value = '';
  emit('update:photo-token', '');
}

/**
 * An upload failure is NOT an answer failure. The uploader hands us the
 * backend's own message (or its Arabic fallback); surface it and leave every
 * answer control live, so she can still answer without the photo.
 */
function onUploadError(error) {
  uploadError.value =
    (typeof error === 'string' ? error : error?.body || error?.main || error?.message) || null;
}

/**
 * Forget a token that has been spent. The parent calls this once an answer is
 * recorded: the token is single-use, so re-sending it on a later revision is a
 * guaranteed 422 rather than a second photo.
 */
function clearPhoto() {
  uploader.value?.clear?.();
  photoToken.value = '';
  uploadError.value = null;
}

defineExpose({ clearPhoto });
</script>

<style scoped>
.answer-note {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

/* Closed, this button IS the entire footprint of the feature. */
.answer-note__toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  align-self: flex-start;
  min-height: 48px;
  padding: 0 4px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: var(--poolit-font-size-sm);
  font-weight: var(--poolit-font-weight-medium);
  color: var(--ci-brand-fg);
  cursor: pointer;
}

/* Explicit, because the first draft fell back to the UA outline (spec Section 8). */
.answer-note__toggle:focus-visible {
  outline: none;
  border-radius: var(--poolit-radius-md);
  box-shadow: 0 0 0 3px var(--ci-brand-ring);
}

.answer-note__plus {
  display: grid;
  place-items: center;
  flex: none;
  width: 20px;
  height: 20px;
  border: 1.5px solid currentColor;
  border-radius: var(--poolit-radius-full);
  font-size: var(--poolit-font-size-sm);
  line-height: 1;
}

.answer-note__block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

/* A real <label>, visually hidden: the toggle and the hint already say what this
   is on screen, so a third visible label would be noise — but the field still
   needs a programmatic name. */
.answer-note__label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.answer-note__field {
  width: 100%;
  min-height: 62px;
  padding: 10px 12px;
  border: 1.5px solid var(--ci-control-border);
  border-radius: var(--poolit-radius-md);
  background: var(--ci-surface);
  color: var(--poolit-text-primary);
  font-family: inherit;
  font-size: var(--poolit-font-size-md);
  line-height: 1.55;
  text-align: start;
  resize: vertical;
}

.answer-note__field:focus-visible {
  outline: none;
  border-color: var(--poolit-primary-500);
  box-shadow: 0 0 0 3px var(--ci-brand-ring);
}

.answer-note__photo {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-wrap: wrap;
}

/* The uploader ships its own 90×90 dashed trigger; only its border and radius
   need to join this card's system. Reaching in with :deep() is deliberate and
   narrow — the alternative was a second file-picker call site. */
.answer-note__photo :deep(.avatar-trigger) {
  width: 90px;
  height: 90px;
  border-radius: var(--poolit-radius-md);
  border: 1.5px dashed var(--ci-control-border);
  background: var(--ci-surface);
}

.answer-note__photo :deep(.avatar-label) {
  font-size: var(--poolit-font-size-2xs);
  color: var(--poolit-text-secondary);
}

/* Her photo from an earlier answer. */
.answer-note__prior {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
  margin: 0;
}

.answer-note__prior img {
  width: 44px;
  height: 44px;
  flex: none;
  object-fit: cover;
  border-radius: var(--poolit-radius-md);
  border: 1px solid var(--ci-border);
}

.answer-note__error {
  margin: 0;
  font-size: var(--poolit-font-size-xs);
  line-height: 1.5;
  color: var(--ci-danger-fg);
}

.answer-note__hint {
  margin: 0;
  font-size: var(--poolit-font-size-xs);
  line-height: 1.5;
  color: var(--poolit-text-muted);
}
</style>
